import React, { useEffect, useMemo, useRef, useState } from "react";
import { computeFEFOSplit, applyFEFOSplit, makeLineId } from "@/lib/fefoSplit";
import { AutoSplitFEFODialog } from "@/components/AutoSplitFEFODialog";
import { StockAlternativasDialog, ConfirmSalirDialog } from "@/components/StockAlternativasDialog";
import { DireccionEntregaSelect } from "@/components/ClienteContactos";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, FileText, ArrowLeft, Save } from "lucide-react";
import { ClienteCombobox } from "@/components/ClienteCombobox";
import {
  SalesLineRow, SalesLineCard, emptySalesLine, recalcSubtotal, applyProductoInfoToLine,
  calcResumenCaja, ResumenCajaBar,
} from "@/components/SalesLineRow";

function emptyForm() {
  return {
    cliente_id: "",
    fecha: new Date().toISOString().slice(0, 10),
    observaciones: "",
    direccion_entrega: "", direccion_entrega_etiqueta: "",
    tiempo_validez_dias: 15,
    dias_entrega: 0,
    forma_pago: "",
    lineas: [],
  };
}

export default function QuotationForm() {
  const { id: editingId } = useParams();
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [c, p] = await Promise.all([
          api.get("/masters/clientes"),
          api.get("/masters/productos-comerciales"),
        ]);
        if (!mounted) return;
        setClientes(c.data.filter(x => x.estado === "aprobado"));
        setProductos(p.data);

        if (editingId) {
          const { data: lista } = await api.get("/sales/cotizaciones");
          const it = lista.find(x => x.id === editingId);
          if (!it) {
            toast.error("Cotización no encontrada");
            navigate("/cotizaciones", { replace: true });
            return;
          }
          if (it.estado === "convertida") {
            toast.error("No se puede editar una cotización convertida");
            navigate("/cotizaciones", { replace: true });
            return;
          }
          setForm({
            cliente_id: it.cliente_id,
            fecha: it.fecha || new Date().toISOString().slice(0, 10),
            observaciones: it.observaciones || "",
            direccion_entrega: it.direccion_entrega || "",
            direccion_entrega_etiqueta: it.direccion_entrega_etiqueta || "",
            numero: it.numero || "",
            estado: it.estado || "",
            tiempo_validez_dias: it.tiempo_validez_dias ?? 15,
            dias_entrega: it.dias_entrega ?? 0,
            forma_pago: it.forma_pago || "",
            lineas: (it.lineas || []).map((l, i) => {
              const prod = p.data.find(x => x.id === l.producto_comercial_id);
              const unidadesPorCaja = l.unidades_por_caja || prod?.unidades_por_presentacion || 1;
              return {
                ...emptySalesLine(),
                ...l,
                _lineId: l._lineId || l.id || `l-edit-${i}`,
                descuento_pct: l.descuento_pct ?? (l.descuento && l.cantidad && l.precio_unitario ? +(100 * l.descuento / (l.cantidad * l.precio_unitario)).toFixed(2) : 0),
                _unidad_venta: l.unidad_venta || "unidad",
                _unidades_por_caja: unidadesPorCaja,
                _precio_unidad: prod?.precio_venta || 0,
                _precio_caja: (prod?.precio_venta || 0) * unidadesPorCaja,
                _pvp_unidad: prod?.pvp || 0,
                _pvp_caja: prod?.pvp_caja || (prod?.pvp || 0) * unidadesPorCaja,
                _precio_minimo_unidad: prod?.precio_minimo || 0,
                _precio_minimo_caja: prod?.precio_minimo_caja || 0,
                _costo_unidad: prod?.precio_costo || 0,
                _margen_aplicado: prod?.margen || 0,
                _margen_fuente: "producto",
                iva_porc: l.iva_porc ?? (prod?.iva_exento ? -1 : (prod?.iva_porcentaje ?? 0)),
              };
            }),
          });
        }
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [editingId, navigate]);

  const addLinea = () => setForm(f => ({ ...f, lineas: [...f.lineas, emptySalesLine()] }));
  const removeLinea = (idx) => setForm(f => ({ ...f, lineas: f.lineas.filter((_, i) => i !== idx) }));

  // ITER170: auto-split FEFO
  const [splitProposal, setSplitProposal] = useState(null);
  const detectFEFOSplit = (idx, updatedLine) => {
    if (!updatedLine.lote_id) return;
    const cant = Number(updatedLine.cantidad) || 0;
    const split = computeFEFOSplit(updatedLine, cant);
    if (split && split.plan.length > 1) {
      setSplitProposal({ idx, split, producto_nombre: updatedLine.producto_nombre });
    }
  };
  const applyProposedSplit = () => {
    if (!splitProposal) return;
    setForm(f => ({
      ...f,
      lineas: applyFEFOSplit(f.lineas, splitProposal.idx, splitProposal.split, recalcSubtotal, makeLineId),
    }));
    setSplitProposal(null);
  };

  const updateLinea = (idx, field, value) => {
    setForm(f => {
      const newL = [...f.lineas];
      newL[idx] = { ...newL[idx], [field]: value };
      newL[idx] = recalcSubtotal(newL[idx]);
      if (field === "cantidad" || field === "unidad_venta") setTimeout(() => detectFEFOSplit(idx, newL[idx]), 250);
      return { ...f, lineas: newL };
    });
  };

  const onProductoChange = async (idx, productoId) => {
    setForm(f => {
      const nl = [...f.lineas];
      nl[idx] = { ...nl[idx], producto_comercial_id: productoId, producto_nombre: "Cargando…" };
      return { ...f, lineas: nl };
    });
    try {
      const params = form.cliente_id ? `?cliente_id=${form.cliente_id}` : "";
      const { data } = await api.get(`/sales/producto-info/${productoId}${params}`);
      setForm(f => {
        const nl = [...f.lineas];
        const cur = nl[idx];
        if (cur && cur.producto_comercial_id === productoId) {
          nl[idx] = applyProductoInfoToLine(cur, data);
          const unidadDef = nl[idx]._unidad_venta;
          nl[idx].precio_unitario = unidadDef === "caja" ? (data.precio_caja || 0) : (data.precio_unidad || data.precio_venta || 0);
          nl[idx] = recalcSubtotal(nl[idx]);
        }
        return { ...f, lineas: nl };
      });
    } catch (e) { console.warn("producto-info failed", e); }
  };

  const onLoteChange = (idx, loteId) => setForm(f => {
    const nl = [...f.lineas];
    const lt = (nl[idx]._lotes_disponibles || []).find(x => x.id === loteId);
    if (lt) {
      nl[idx] = {
        ...nl[idx],
        lote_id: lt.id,
        numero_lote: lt.numero_lote || "",
        fecha_elaboracion: lt.fecha_elaboracion || "",
        fecha_vencimiento: lt.fecha_vencimiento || "",
      };
    }
    return { ...f, lineas: nl };
  });

  const toggleUnidadVenta = (idx, modo) => setForm(f => {
    const nl = [...f.lineas];
    nl[idx] = {
      ...nl[idx],
      _unidad_venta: modo,
      precio_unitario: modo === "caja" ? (nl[idx]._precio_caja || 0) : (nl[idx]._precio_unidad || 0),
    };
    nl[idx] = recalcSubtotal(nl[idx]);
    return { ...f, lineas: nl };
  });

  const totales = useMemo(() => {
    const subtotal = form.lineas.reduce((s, l) => s + (parseFloat(l.subtotal) || 0), 0);
    const iva = form.lineas.reduce((s, l) => s + (parseFloat(l.subtotal) || 0) * (Math.max(parseFloat(l.iva_porc) || 0, 0) / 100), 0);
    return { subtotal: +subtotal.toFixed(2), iva: +iva.toFixed(2), total: +(subtotal + iva).toFixed(2) };
  }, [form.lineas]);
  const resumenCaja = useMemo(() => calcResumenCaja(form.lineas), [form.lineas]);


  // ITER221: chequeo de stock en otro laboratorio antes de guardar
  const [sugerencias, setSugerencias] = useState([]);
  const [confirmSalir, setConfirmSalir] = useState(false);
  const snapshotRef = useRef(null);
  const esDirty = () => snapshotRef.current !== null && snapshotRef.current !== JSON.stringify(form);

  const reemplazarPorAlternativa = async (idx, alt) => {
    await onProductoChange(idx, alt.producto_comercial_id);
    setSugerencias(prev => prev.filter(s => s.linea_idx !== idx));
    toast.warning(`Producto reemplazado por ${alt.nombre_comercial}. Revise las cantidades de esa línea.`, { duration: 8000 });
  };

  const chequearAlternativas = async () => {
    try {
      const { data } = await api.post("/sales/verificar-alternativas-stock", {
        cliente_id: form.cliente_id || null,
        excluir_pedido_id: editingId || null,
        lineas: form.lineas.filter(l => l.producto_comercial_id).map(l => ({
          producto_comercial_id: l.producto_comercial_id,
          cantidad: Number(l.cantidad) || 0,
          unidad_venta: l._unidad_venta || "unidad",
          unidades_por_caja: l._unidades_por_caja || 1,
          producto_nombre: l.producto_nombre || "",
        })),
      });
      return data?.sugerencias || [];
    } catch (e) { return []; }
  };

  useEffect(() => {
    if (!loading && snapshotRef.current === null) snapshotRef.current = JSON.stringify(form);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const save = async (opts = {}) => {
    if (!form.cliente_id) return toast.error("Seleccione un cliente aprobado");
    if (form.lineas.length === 0) return toast.error("Agregue al menos un producto");
    if (!opts.omitirChequeo) {
      setSaving(true);
      const sug = await chequearAlternativas();
      setSaving(false);
      if (sug.length) { setSugerencias(sug); return; }
    }
    // NOTA: En cotizaciones NO se valida precio mínimo ni se exige PIN de
    // gerencia. Esa validación se mantiene solo en facturas electrónicas.
    try {
      setSaving(true);
      const lineas = form.lineas.map((l) => {
        // eslint-disable-next-line no-unused-vars
        const { _lotes_disponibles, _unidad_venta, _unidades_por_caja, _precio_unidad, _precio_caja,
                _pvp_unidad, _pvp_caja, _precio_minimo_unidad, _precio_minimo_caja, _costo_unidad,
                _margen_aplicado, _margen_fuente, _stock_total, _lineId, ...rest } = l;
        return {
          ...rest,
          unidad_venta: _unidad_venta || "unidad",
          unidades_por_caja: _unidades_por_caja || null,
        };
      });
      const payload = { ...form, lineas, permite_stock_cero: true, ...totales };
      if (editingId) {
        await api.put(`/sales/cotizaciones/${editingId}`, payload);
        toast.success("Cotización actualizada");
      } else {
        await api.post("/sales/cotizaciones", payload);
        toast.success("Cotización creada");
      }
      snapshotRef.current = null;
      navigate("/cotizaciones");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const cancelar = () => {
    if (esDirty()) { setConfirmSalir(true); return; }
    navigate("/cotizaciones");
  };

  if (loading) return <div className="p-10 text-center text-[#5c7368]">Cargando…</div>;

  return (
    <div className="space-y-4 pb-40" data-testid="quote-form-page">
      {/* Header sticky */}
      <div className="sticky top-0 z-20 -mx-4 md:-mx-6 px-4 md:px-6 py-3 bg-[#f7f9f8]/95 backdrop-blur border-b border-[#d1dbd6]">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={cancelar} data-testid="quote-form-back-btn" className="flex-shrink-0">
            <ArrowLeft size={18} />
          </Button>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5c7368]">Ventas / Cotizaciones</div>
            <h1 className="text-lg md:text-2xl font-bold tracking-tight text-[#15241d] truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>
              <FileText className="inline mr-2 -mt-0.5" size={20} />
              {editingId ? (
                <>
                  Cotización
                  {form.numero ? (
                    <span className="ml-2 text-[#205c45] font-mono" data-testid="quote-form-numero">N° {form.numero}</span>
                  ) : null}
                  {form.estado ? (
                    <span className="ml-2 text-xs uppercase tracking-wider text-[#5c7368] font-semibold align-middle">· {form.estado}</span>
                  ) : null}
                </>
              ) : "Nueva cotización"}
            </h1>
          </div>
          <div className="hidden md:flex items-center gap-2 flex-shrink-0">
            <Button variant="outline" onClick={cancelar} disabled={saving} data-testid="quote-form-cancel-btn-desktop">Cancelar</Button>
            <Button onClick={() => save()} disabled={saving} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="quote-form-save-btn-desktop">
              <Save size={16} className="mr-2" />
              {saving ? "Guardando…" : (editingId ? "Guardar cambios" : "Guardar")}
            </Button>
          </div>
        </div>
      </div>

      {/* Cabecera */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <Label>Cliente (solo aprobados)</Label>
          <ClienteCombobox
            value={form.cliente_id}
            onChange={(v) => setForm({ ...form, cliente_id: v })}
            clientes={clientes}
            placeholder="Buscar cliente por razón social, RUC o ciudad…"
            testId="quote-client"
          />
        </div>
        <div>
          <Label>Fecha</Label>
          <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="quote-form-fecha" />
        </div>
        <div className="md:col-span-2">
          <DireccionEntregaSelect
            cliente={clientes.find(c => c.id === form.cliente_id)}
            value={form.direccion_entrega}
            etiqueta={form.direccion_entrega_etiqueta}
            onChange={({ direccion, etiqueta }) => setForm(f => ({ ...f, direccion_entrega: direccion, direccion_entrega_etiqueta: etiqueta }))}
            testId="quote-direccion"
          />
        </div>
        <div>
          <Label>Validez de la cotización (días)</Label>
          <Input
            type="number"
            min={0}
            value={form.tiempo_validez_dias ?? 15}
            onChange={(e) => setForm({ ...form, tiempo_validez_dias: e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0) })}
            data-testid="quote-form-validez"
            placeholder="15"
          />
          <div className="text-[11px] text-[#5c7368] mt-1">
            Vence:{" "}
            <span className="font-mono text-[#205c45]" data-testid="quote-form-vence">
              {(() => {
                try {
                  const d = new Date(form.fecha);
                  d.setDate(d.getDate() + Number(form.tiempo_validez_dias || 0));
                  return d.toISOString().slice(0, 10);
                } catch { return "—"; }
              })()}
            </span>
          </div>
        </div>
        <div>
          <Label>Días para la entrega</Label>
          <Input
            type="number"
            min={0}
            value={form.dias_entrega ?? 0}
            onChange={(e) => setForm({ ...form, dias_entrega: e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0) })}
            data-testid="quote-form-entrega"
            placeholder="0 = inmediata"
          />
        </div>
        <div>
          <Label>Forma de pago</Label>
          <Input
            value={form.forma_pago || ""}
            onChange={(e) => setForm({ ...form, forma_pago: e.target.value })}
            placeholder="Contado / Crédito 30 días / Transferencia…"
            data-testid="quote-form-forma-pago"
          />
        </div>
      </div>

      {/* MÓVIL */}
      <div className="space-y-3 md:hidden">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Productos</div>
          <Button size="sm" onClick={addLinea} className="bg-[#205c45] hover:bg-[#1a4a37] h-9" data-testid="add-line-btn-mobile">
            <Plus size={14} className="mr-1" />Agregar
          </Button>
        </div>
        {form.lineas.length === 0 && (
          <div className="rounded-lg border-2 border-dashed border-[#d1dbd6] py-8 text-center text-sm text-[#8ba397]">
            Sin productos. Toque "Agregar" para empezar.
          </div>
        )}
        {form.lineas.map((l, idx) => (
          <SalesLineCard
            key={l._lineId || `lc-${idx}`}
            line={l}
            idx={idx}
            productos={productos}
            onChange={updateLinea}
            onProductoChange={onProductoChange}
            onLoteChange={onLoteChange}
            onRemove={removeLinea}
            onToggleUnidad={toggleUnidadVenta}
            testPrefix="quote"
            showIVA={true}
          />
        ))}
        {form.lineas.length > 0 && (
          <Button size="sm" onClick={addLinea} variant="outline" className="w-full h-10" data-testid="quot-add-line-btn-mobile-bottom">
            <Plus size={14} className="mr-1" />Agregar otro producto
          </Button>
        )}
        {form.lineas.length > 0 && (
          <div className="rounded-lg border border-[#d1dbd6] bg-[#f7f9f8] p-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-[#5c7368]">Subtotal</span><span className="tabular-nums">${totales.subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-[#5c7368]">IVA</span><span className="tabular-nums">${totales.iva.toFixed(2)}</span></div>
            <div className="flex justify-between pt-1 border-t border-[#d1dbd6]"><span className="font-bold uppercase">Total</span><span className="text-xl font-bold tabular-nums">${totales.total.toFixed(2)}</span></div>
          </div>
        )}
      </div>

      {/* ESCRITORIO */}
      <div className="hidden md:block border border-[#d1dbd6] rounded-md">
        <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b border-[#d1dbd6]">
          <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Líneas</div>
          <Button size="sm" variant="outline" onClick={addLinea} data-testid="add-line-btn">
            <Plus size={14} className="mr-1" />Agregar producto
          </Button>
        </div>
        <table className="erp-table" style={{ tableLayout: "fixed", width: "100%" }}>
          <thead>
            <tr>
              <th className="w-8"></th>
              <th style={{ width: "260px" }}>Producto</th>
              <th className="w-40">Cant. + Modo</th>
              <th className="w-28">Precio U.</th>
              <th className="w-20">Desc. %</th>
              <th className="w-24">IVA</th>
              <th className="w-28 text-right">Subtotal</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {form.lineas.map((l, idx) => (
              <SalesLineRow
                key={l._lineId || `l-${idx}`}
                line={l}
                idx={idx}
                productos={productos}
                onChange={updateLinea}
                onProductoChange={onProductoChange}
                onLoteChange={onLoteChange}
                onRemove={removeLinea}
                onToggleUnidad={toggleUnidadVenta}
                testPrefix="quote"
                showIVA={true}
              />
            ))}
            {form.lineas.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-[#8ba397] text-sm">Sin líneas. Agregue productos.</td></tr>}
            {form.lineas.length > 0 && (
              <tr>
                <td colSpan={8} className="p-2 bg-[#f7fbf9] border-t border-[#d1dbd6]">
                  <Button size="sm" variant="outline" onClick={addLinea} className="w-full" data-testid="quot-add-line-btn-bottom">
                    <Plus size={14} className="mr-1" />Agregar producto
                  </Button>
                </td>
              </tr>
            )}
          </tbody>
          <tfoot className="bg-[#f7f9f8] border-t border-[#d1dbd6]">
            <tr><td colSpan={6} className="text-right font-semibold uppercase text-xs tracking-wider text-[#5c7368] px-3 py-1">Subtotal</td><td colSpan={2} className="text-right tabular-nums px-3 py-1">${totales.subtotal.toFixed(2)}</td></tr>
            <tr><td colSpan={6} className="text-right font-semibold uppercase text-xs tracking-wider text-[#5c7368] px-3 py-1">IVA</td><td colSpan={2} className="text-right tabular-nums px-3 py-1">${totales.iva.toFixed(2)}</td></tr>
            <tr><td colSpan={6} className="text-right font-bold uppercase text-xs tracking-wider text-[#5c7368] px-3 py-2">Total</td><td colSpan={2} className="text-right font-bold text-lg tabular-nums px-3 py-2">${totales.total.toFixed(2)}</td></tr>
          </tfoot>
        </table>
      </div>

      <ResumenCajaBar resumen={resumenCaja} />

      <div>
        <Label>Observaciones</Label>
        <Input value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} data-testid="quote-form-observaciones" />
      </div>

      {/* Barra fija inferior móvil — bottom-[64px] deja espacio al badge "Made with Emergent" en preview */}
      <div className="fixed bottom-[64px] left-0 right-0 z-[60] border-t border-[#d1dbd6] bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.08)] md:hidden">
        <div className="flex items-center justify-between gap-2 px-3 py-2">
          <div className="flex flex-col text-right pr-1 leading-tight">
            <span className="text-[10px] uppercase tracking-wider text-[#5c7368]">Total</span>
            <span className="text-lg font-bold tabular-nums text-[#15241d]">${totales.total.toFixed(2)}</span>
          </div>
          <div className="flex gap-2 flex-1 justify-end">
            <Button variant="outline" className="h-11 flex-1 max-w-[120px]" onClick={cancelar} disabled={saving} data-testid="quote-form-cancel-btn">Cancelar</Button>
            <Button onClick={() => save()} disabled={saving} className="bg-[#205c45] hover:bg-[#1a4a37] h-11 flex-1" data-testid="quote-form-save-btn">
              <Save size={16} className="mr-2" />
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </div>
        </div>
      </div>

      <StockAlternativasDialog
        open={sugerencias.length > 0}
        sugerencias={sugerencias}
        saving={saving}
        onReemplazar={reemplazarPorAlternativa}
        onGuardarIgual={() => { setSugerencias([]); save({ omitirChequeo: true }); }}
        onCerrar={() => setSugerencias([])}
      />

      <ConfirmSalirDialog
        open={confirmSalir}
        mensaje="Tiene cambios sin guardar en esta cotización. ¿Desea salir y descartarlos?"
        onConfirmar={() => { setConfirmSalir(false); snapshotRef.current = null; navigate("/cotizaciones"); }}
        onCancelar={() => setConfirmSalir(false)}
      />

      <AutoSplitFEFODialog
        open={!!splitProposal}
        split={splitProposal?.split}
        productoNombre={splitProposal?.producto_nombre}
        onConfirm={applyProposedSplit}
        onCancel={() => setSplitProposal(null)}
      />
    </div>
  );
}
